import React, { useCallback, useState } from 'react';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Upload, X, Camera } from 'lucide-react';

export const ProjectDetailsStep = ({ formData, updateFormData }) => {
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  }, []);

  const handleFileInput = (e) => {
    if (e.target.files) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const handleFiles = (files) => {
    const validFiles = files.filter(file =>
      file.type.startsWith('image/') && file.size <= 10 * 1024 * 1024
    );

    const newPhotos = validFiles.map(file => ({
      id: Date.now() + Math.random(),
      file: file,
      url: URL.createObjectURL(file),
      name: file.name,
      size: file.size
    }));

    updateFormData({
      photos: [...formData.photos, ...newPhotos].slice(0, 6)
    });
  };

  const removePhoto = (photoId) => {
    const updatedPhotos = formData.photos.filter(photo => photo.id !== photoId);
    updateFormData({ photos: updatedPhotos });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-8 mb-12">
      {/* Header */}
      <div className="text-center">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          Upload Photos
        </h3>
        <p className="text-gray-600">
          Photos are optional. You can skip this step if you don't have any right now.
        </p>
      </div>

      {/* Photo Upload */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Label className="text-base font-medium text-gray-900">
            Project Photos
          </Label>
          <Badge variant="outline" className="text-xs">
            {formData.photos.length}/6 photos
          </Badge>
        </div>

        {/* Upload Area */}
        <div
          className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 ${
            dragActive
              ? 'border-emerald-500 bg-emerald-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <input
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileInput}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          />

          <div className="space-y-4">
            <div className="bg-gray-100 w-16 h-16 rounded-full mx-auto flex items-center justify-center">
              <Upload className="w-8 h-8 text-gray-600" />
            </div>

            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-2">
                Drop photos here or click to upload
              </h4>
              <p className="text-gray-600">
                Upload up to 6 photos (Max 10MB each)
              </p>
            </div>

            <Button variant="outline" className="mt-4">
              <Camera className="w-4 h-4 mr-2" />
              Choose Photos
            </Button>
          </div>
        </div>

        {/* Photo Preview Grid */}
        {formData.photos.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {formData.photos.map((photo) => (
              <div key={photo.id} className="relative group">
                <div className="aspect-square rounded-lg overflow-hidden bg-gray-100 border-2 border-gray-200">
                  <img
                    src={photo.url}
                    alt={photo.name}
                    className="w-full h-full object-cover"
                  />
                </div>

                <button
                  onClick={() => removePhoto(photo.id)}
                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 hover:bg-red-600"
                >
                  <X size={14} />
                </button>

                <div className="mt-2 text-center">
                  <p className="text-xs text-gray-600 truncate">{photo.name}</p>
                  <p className="text-xs text-gray-500">{formatFileSize(photo.size)}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
